import { Avatar } from 'antd'
import {
  Briefcase,
  ChartCandlestick,
  CircleCheckBig,
  FileText,
  FolderOpen,
  LayoutGrid,
  Package,
  Rocket,
  ShoppingCart,
  UserCheck,
  Users
} from 'lucide-react'
import type { ComponentType } from 'react'

/**
 * Per-app-type icon + brand color, ported from
 * `WEB/apps/organization/src/layout/Applications/AppIcons/{AppIcons,utils,styles}`.
 *
 * Icons are mapped to the closest lucide-react equivalents (the same Lucide
 * shapes WEB renders inline as SVGs); colors match WEB's `colors` constant.
 */

export const APP_TYPE = {
  Default: 'default',
  Cms: 'cms',
  Crm: 'crm',
  Dms: 'dms',
  Hrms: 'hrm',
  HrmsEm: 'hrm-em',
  RocketDesigner: 'rocket-designer',
  Tms: 'tms',
  Warehouse: 'warehouse',
  CrmFinance: 'crm-finance',
  Ecommerce: 'ecommerce',
  ThirdPartyCustom: 'third_party_custom'
} as const

type IconConfig = {
  icon: ComponentType<{ size?: number }>
  color: string
}

const APP_TYPE_CONFIG: Record<string, IconConfig> = {
  [APP_TYPE.Default]: { icon: LayoutGrid, color: '#6B7280' },
  [APP_TYPE.Cms]: { icon: FileText, color: '#7c3aed' },
  [APP_TYPE.Crm]: { icon: Users, color: '#3b82f6' },
  [APP_TYPE.Dms]: { icon: FolderOpen, color: '#8b5cf6' },
  [APP_TYPE.Hrms]: { icon: UserCheck, color: '#06b6d4' },
  [APP_TYPE.HrmsEm]: { icon: Briefcase, color: '#10b981' },
  [APP_TYPE.RocketDesigner]: { icon: Rocket, color: '#dc2626' },
  [APP_TYPE.Tms]: { icon: CircleCheckBig, color: '#8b5cf6' },
  [APP_TYPE.Warehouse]: { icon: Package, color: '#f97316' },
  [APP_TYPE.CrmFinance]: { icon: ChartCandlestick, color: '#0ea5e9' },
  [APP_TYPE.Ecommerce]: { icon: ShoppingCart, color: '#f59e0b' },
  [APP_TYPE.ThirdPartyCustom]: { icon: LayoutGrid, color: '#6366f1' }
}

const resolveConfig = (type: string | null | undefined): IconConfig =>
  APP_TYPE_CONFIG[type ?? ''] ?? APP_TYPE_CONFIG[APP_TYPE.Default]

/** Chrome shared with `AppIcon` so a custom icon sits in the same plate. */
export const APP_ICON_BORDER_RADIUS = 8
export const APP_ICON_GLYPH_RATIO = 0.45

type Props = {
  type: string | null | undefined
  size?: number
}

export const AppTypeIcon = ({ type, size = 40 }: Props) => {
  const { icon: Icon, color } = resolveConfig(type)

  return (
    <Avatar
      size={size}
      shape="square"
      icon={<Icon size={Math.round(size * APP_ICON_GLYPH_RATIO)} />}
      style={{
        backgroundColor: color,
        borderRadius: APP_ICON_BORDER_RADIUS,
        minWidth: size
      }}
    />
  )
}
